import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Close,
  Cancel,
  ImageNotSupported,
  ShieldOutlined,
  FaceOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import type { IdentityVerification } from "./types";
import { reviewIdentityVerification, buildDocUrl, DOC_TYPE_LABELS } from "./identityVerificationApi";

const TEAL = "#079889";

const STATUS_CHIP: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  pending: { label: "Pending Review", bg: "#fef3c7", color: "#92400e" },
  approved: { label: "Approved", bg: "#d1fae5", color: "#065f46" },
  rejected: { label: "Rejected", bg: "#fee2e2", color: "#991b1b" },
};

interface Props {
  record: IdentityVerification | null;
  open: boolean;
  onClose: () => void;
  onUpdated: (updated: IdentityVerification) => void;
}

function DocImage({
  url,
  label,
  icon,
}: {
  url: string | null;
  label: string;
  icon: React.ReactNode;
}) {
  const [errored, setErrored] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const src = buildDocUrl(url);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ color: TEAL, display: "flex" }}>{icon}</Box>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {label}
        </Typography>
      </Box>
      {src && !errored ? (
        <>
          <Box
            component="img"
            src={src}
            alt={label}
            onError={() => setErrored(true)}
            onClick={() => setZoomed(true)}
            sx={{
              width: "100%",
              height: 180,
              objectFit: "cover",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              cursor: "zoom-in",
              transition: "transform 0.15s",
              "&:hover": { transform: "scale(1.01)" },
            }}
          />
          {/* Lightbox */}
          {zoomed &&
            createPortal(
              <Box
                onClick={() => setZoomed(false)}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 2000,
                  bgcolor: "rgba(0,0,0,0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "zoom-out",
                  p: 2,
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={label}
                  sx={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    borderRadius: 2,
                    boxShadow: 24,
                  }}
                />
              </Box>,
              document.body,
            )}
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 180,
            bgcolor: "#f3f4f6",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: "1px dashed #d1d5db",
          }}
        >
          <ImageNotSupported sx={{ color: "#9ca3af", fontSize: 32 }} />
          <Typography variant="caption" color="text.secondary">
            No image submitted
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function VerificationDetailModal({
  record,
  open,
  onClose,
  onUpdated,
}: Props) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState<"approve" | "reject" | null>(null);

  if (!record) return null;

  const chip = STATUS_CHIP[record.status] ?? STATUS_CHIP.pending;
  const canReview = record.status === "pending";

  const handleApprove = async () => {
    setSaving("approve");
    try {
      const updated = await reviewIdentityVerification(record.id, "approve");
      toast.success("Identity verified — account approved.");
      onUpdated(updated);
      onClose();
    } catch {
      toast.error("Failed to approve. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a rejection reason.");
      return;
    }
    setSaving("reject");
    try {
      const updated = await reviewIdentityVerification(
        record.id,
        "reject",
        reason.trim(),
      );
      toast.success("Verification rejected.");
      onUpdated(updated);
      onClose();
    } catch {
      toast.error("Failed to reject. Please try again.");
    } finally {
      setSaving(null);
      setRejecting(false);
      setReason("");
    }
  };

  const handleClose = () => {
    if (saving) return;
    setRejecting(false);
    setReason("");
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 560,
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 20,
              background: "#fff",
              boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Identity Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {record.user_full_name || record.user_email}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={chip.label}
                    size="small"
                    sx={{
                      bgcolor: chip.bg,
                      color: chip.color,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                  <Box
                    component="button"
                    onClick={handleClose}
                    sx={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                      borderRadius: "50%",
                      color: "text.secondary",
                      "&:hover": { bgcolor: "#f3f4f6" },
                    }}
                  >
                    <Close fontSize="small" />
                  </Box>
                </Box>
              </Box>

              {/* Meta row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  mb: 2.5,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Document Type
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {record.id_document_type
                      ? DOC_TYPE_LABELS[record.id_document_type] ??
                        record.id_document_type
                      : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Submitted
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {record.submitted_at
                      ? new Date(record.submitted_at).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )
                      : "—"}
                  </Typography>
                </Box>
                {record.reviewed_at && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Reviewed
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(record.reviewed_at).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Rejection reason (if rejected) */}
              {record.status === "rejected" && record.rejection_reason && (
                <Box
                  sx={{
                    bgcolor: "#fff1f2",
                    border: "1px solid #fecdd3",
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#b91c1c"
                    fontWeight={600}
                  >
                    Rejection Reason
                  </Typography>
                  <Typography variant="body2" color="#991b1b" mt={0.5}>
                    {record.rejection_reason}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ mb: 2.5 }} />

              {/* Document images side by side */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
                <DocImage
                  url={record.id_document}
                  label="ID Document"
                  icon={<ShieldOutlined fontSize="small" />}
                />
                <DocImage
                  url={record.selfie_photo}
                  label="Selfie"
                  icon={<FaceOutlined fontSize="small" />}
                />
              </Box>

              {/* Actions */}
              {canReview && (
                <>
                  <Divider sx={{ mb: 2 }} />

                  {rejecting ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <TextField
                        label="Rejection reason"
                        placeholder="Explain why the documents were rejected…"
                        multiline
                        minRows={2}
                        fullWidth
                        size="small"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setRejecting(false);
                            setReason("");
                          }}
                          disabled={!!saving}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            flex: 1,
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          onClick={handleReject}
                          disabled={!!saving || !reason.trim()}
                          startIcon={
                            saving === "reject" ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <Cancel fontSize="small" />
                            )
                          }
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            flex: 2,
                          }}
                        >
                          Confirm Rejection
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => setRejecting(true)}
                        disabled={!!saving}
                        startIcon={<Cancel fontSize="small" />}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleApprove}
                        disabled={!!saving}
                        startIcon={
                          saving === "approve" ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <CheckCircle fontSize="small" />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          bgcolor: TEAL,
                          "&:hover": { bgcolor: "#057a6e" },
                        }}
                      >
                        Approve
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>,
    document.body,
  );
}
